/**
 * Theme Context - Manages application theme state and persistence
 * Version: 6.2.0
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeName = 'dark' | 'light' | 'ocean' | 'forest' | 'sunset' | 'midnight' | 'neon' | 'royal' | 'emerald' | 'lavender' | 'rosegold' | 'arctic' | 'fintech-dark';

export interface Theme {
  name: ThemeName;
  label: string;
  icon: string;
  description: string;
  category: 'dark' | 'light' | 'colorful';
}

export const THEMES: Record<ThemeName, Theme> = {
  dark: {
    name: 'dark',
    label: 'Dark Cyberpunk',
    icon: '🌙',
    description: 'Deep space dark theme with purple accents',
    category: 'dark',
  },
  light: {
    name: 'light',
    label: 'Light Clean',
    icon: '☀️',
    description: 'Bright and clean light theme',
    category: 'light',
  },
  ocean: {
    name: 'ocean',
    label: 'Ocean Blue',
    icon: '🌊',
    description: 'Calming ocean-inspired blue theme',
    category: 'dark',
  },
  forest: {
    name: 'forest',
    label: 'Forest Green',
    icon: '🌲',
    description: 'Nature-inspired green theme',
    category: 'dark',
  },
  sunset: {
    name: 'sunset',
    label: 'Sunset Warm',
    icon: '🌅',
    description: 'Warm sunset-inspired orange theme',
    category: 'dark',
  },
  midnight: {
    name: 'midnight',
    label: 'Midnight Purple',
    icon: '✨',
    description: 'Royal purple midnight theme',
    category: 'dark',
  },
  neon: {
    name: 'neon',
    label: 'Neon City',
    icon: '💜',
    description: 'Vibrant neon cyberpunk theme',
    category: 'dark',
  },
  royal: {
    name: 'royal',
    label: 'Royal Blue',
    icon: '👑',
    description: 'Elegant navy blue theme',
    category: 'dark',
  },
  emerald: {
    name: 'emerald',
    label: 'Emerald Green',
    icon: '💎',
    description: 'Rich emerald green theme',
    category: 'dark',
  },
  lavender: {
    name: 'lavender',
    label: 'Lavender Purple',
    icon: '💜',
    description: 'Soft violet purple theme',
    category: 'dark',
  },
  rosegold: {
    name: 'rosegold',
    label: 'Rose Gold',
    icon: '🌹',
    description: 'Warm rose gold theme',
    category: 'dark',
  },
  arctic: {
    name: 'arctic',
    label: 'Arctic Frost',
    icon: '❄️',
    description: 'Cool arctic silver theme',
    category: 'light',
  },
  'fintech-dark': {
    name: 'fintech-dark',
    label: 'Premium Fintech',
    icon: '📈',
    description: 'TradingView/Binance-inspired professional theme',
    category: 'dark',
  },
};

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themes: typeof THEMES;
  getThemeInfo: (themeName: ThemeName) => Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'zobull-theme';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    // Try to get theme from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored in THEMES) {
        return stored as ThemeName;
      }
    }
    return 'dark';
  });

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, theme);
    // Dispatch custom event for any listeners
    window.dispatchEvent(new CustomEvent('themechange', { detail: { theme } }));
  }, [theme]);

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
  };

  const getThemeInfo = (themeName: ThemeName): Theme => {
    return THEMES[themeName];
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES, getThemeInfo }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook for components that only need to react to theme changes
export const useThemeValue = (): ThemeName => {
  const { theme } = useTheme();
  return theme;
};